<template>
	<v-main>
		<!-- barra che non si stampa -->
		<div class="fp-toolbar no-print d-flex align-center px-4 py-2">
			<v-btn small text @click="back">
				<v-icon small left>mdi-arrow-left</v-icon>
				{{ backLabel }}
			</v-btn>
			<v-spacer />
			<span class="text-caption grey--text mr-3">Nella finestra di stampa puoi anche scegliere "Salva come PDF"</span>
			<v-btn small depressed color="primary" :disabled="!report" @click="stampa">
				<v-icon small left>mdi-printer</v-icon>
				Stampa / Salva PDF
			</v-btn>
		</div>

		<div class="fp-page">
			<v-skeleton-loader v-if="!report" type="article, table" />
			<template v-else>
				<header class="fp-header">
					<div>
						<div class="text-overline grey--text">Quadro OSS · vendite a distanza B2C UE</div>
						<h1 class="fp-title">{{ title }}</h1>
						<div class="fp-muted">
							{{ report.period.ossActive ? 'Importi da dichiarare per Paese di consumo e aliquota' : 'Simulazione: nel periodo non risulta l’adesione all’OSS' }}
						</div>
					</div>
					<div class="text-right fp-muted">
						Stampato il {{ new Date() | moment('DD/MM/YYYY') }}
						<div v-if="report.period.ecbDate">Cambio BCE del {{ report.period.ecbDate | moment('DD/MM/YYYY') }}</div>
					</div>
				</header>

				<table class="fp-table">
					<thead>
						<tr>
							<th class="text-left">Paese</th>
							<th class="text-left">Tipo aliquota</th>
							<th class="text-right">Aliquota</th>
							<th class="text-right">Imponibile</th>
							<th class="text-right">IVA</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="r in report.oss" :key="`${r.country}:${r.rate}`" class="avoid-break">
							<td>{{ r.country }} · {{ r.name }}</td>
							<td>{{ r.rateType === 'STANDARD' ? 'Ordinaria' : 'Ridotta' }}</td>
							<td class="text-right">{{ formatPct(r.rate) }}</td>
							<td class="text-right">{{ formatEurCents(r.net) }}</td>
							<td class="text-right">{{ formatEurCents(r.vat) }}</td>
						</tr>
						<tr v-if="!report.oss.length">
							<td colspan="5" class="text-center grey--text py-4">Nessuna vendita B2C verso altri Paesi UE nel periodo</td>
						</tr>
					</tbody>
					<tfoot v-if="report.oss.length">
						<tr>
							<td colspan="3" class="text-right font-weight-medium">Totale</td>
							<td class="text-right font-weight-medium">{{ formatEurCents(report.summary.oss.net) }}</td>
							<td class="text-right font-weight-medium">{{ formatEurCents(report.summary.oss.vat) }}</td>
						</tr>
					</tfoot>
				</table>

				<template v-if="report.corrections.length">
					<h2 class="fp-subtitle">Rettifiche di periodi precedenti</h2>
					<table class="fp-table">
						<thead>
							<tr>
								<th class="text-left">Paese</th>
								<th class="text-left">Trimestre d’origine</th>
								<th class="text-right">Aliquota</th>
								<th class="text-right">Imponibile</th>
								<th class="text-right">IVA</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="c in report.corrections" :key="`${c.country}:${c.period}:${c.rate}`" class="avoid-break">
								<td>{{ c.country }}</td>
								<td>{{ c.period.replace('-Q', ' T') }}</td>
								<td class="text-right">{{ formatPct(c.rate) }}</td>
								<td class="text-right">{{ formatEurCents(c.net) }}</td>
								<td class="text-right">{{ formatEurCents(c.vat) }}</td>
							</tr>
						</tbody>
					</table>
				</template>

				<div class="fp-muted mt-4">
					Totale IVA da versare con la dichiarazione OSS: <strong>{{ formatEurCents(report.summary.oss.vat + (report.summary.corrections.vat || 0)) }}</strong
					>. Report indicativo, da verificare con il commercialista prima dell’invio.
				</div>
			</template>
		</div>
	</v-main>
</template>

<script>
import { mapActions } from 'vuex';
import { returnInfo, goBackWithResult } from '@/components/core/pageReturn';
import { formatEurCents, formatPct } from '@/components/finance/format';
import { quarterLabel } from '@/components/finance/foreign/labels';

export default {
	name: 'FinanceForeignPrint',
	data() {
		return { report: null, ret: returnInfo(this.$route) };
	},
	computed: {
		backLabel() {
			return this.ret ? this.ret.label : 'Torna al report IVA estera';
		},
		title() {
			return quarterLabel(this.report.period.year, this.report.period.quarter);
		},
	},
	async mounted() {
		try {
			const q = +this.$route.query.quarter || undefined;
			this.report = await this.fetchReport({ year: +this.$route.query.year || undefined, quarter: q });
		} catch (err) {
			this.$store.dispatch('settings/updateSnackbar', {
				color: 'error',
				text: (err.response && err.response.data && err.response.data.message) || err.message,
				snackbar: true,
				mode: 'multi-line',
				timeout: 5000,
			});
		}
	},
	methods: {
		...mapActions({ fetchReport: 'finance/fetchForeignReport' }),
		formatEurCents,
		formatPct,
		stampa() {
			window.print();
		},
		back() {
			if (this.ret) goBackWithResult(this.$router, this.ret, '', null);
			else this.$router.push({ path: '/finance/foreign', query: { tab: 'oss' } });
		},
	},
};
</script>

<style scoped>
.fp-toolbar {
	border-bottom: 1px solid #e1e7ef;
	background: #fff;
}
.fp-page {
	max-width: 1000px;
	margin: 0 auto;
	padding: 24px;
}
.fp-header {
	display: flex;
	justify-content: space-between;
	gap: 24px;
	margin-bottom: 20px;
}
.fp-title {
	font-size: 22px;
	font-weight: 600;
}
.fp-subtitle {
	font-size: 15px;
	font-weight: 600;
	margin: 20px 0 8px;
}
.fp-muted {
	font-size: 12px;
	color: #5f6b7a;
}
.fp-table {
	width: 100%;
	border-collapse: collapse;
	font-size: 13px;
}
.fp-table th,
.fp-table td {
	padding: 6px 8px;
	border-bottom: 1px solid #e0e0e0;
}
.fp-table th {
	font-size: 12px;
	color: #5f6b7a;
	font-weight: 500;
}
</style>

<style>
@media print {
	@page {
		size: A4;
		margin: 12mm;
	}
	.no-print,
	.v-navigation-drawer,
	.v-app-bar,
	.v-footer {
		display: none !important;
	}
	.v-main {
		padding: 0 !important;
	}
	.fp-page {
		max-width: none !important;
		padding: 0 !important;
	}
	.avoid-break {
		break-inside: avoid;
		page-break-inside: avoid;
	}
}
</style>
